/**
 * Enter Task reason parsing — ported from Enter Platform's own Tasks UI so the
 * two products read a task the same way.
 *
 * WHY THIS EXISTS: Enter's ProviderMessage has no structured "reason" field.
 * When the coding engine halts a claim it writes the detail into the opening
 * COMMENT as a fenced dump:
 *
 *     The coding engine has returned the following error:
 *     ```
 *     providers [0] is invalid.
 *       Invalid NPI: 1234567890
 *     ```
 *
 * Rendering that verbatim — which is what we did — shows the user a wall of
 * machine output. Platform parses it into "Why it's halted" with a fix-list.
 * This is a faithful port of `comment-reason.utils.ts` + the classification
 * helpers from `coding-reason-card.tsx` / `resolve-section.tsx` in
 * enter-front-end (packages/core/src/ui/messaging/chat-panel/).
 *
 * The REGEXES AND MARKER STRINGS ARE A WIRE CONTRACT with Enter's backend and
 * with text their engine emits — they are copied exactly and must not be
 * "cleaned up". Keep it dependency-free (platform uses lodash; we inline it) so
 * both clients and the gateway can use it.
 */

// ─── Resolve markers ────────────────────────────────────────────────────────
// Sentinel comment bodies. Posting one of these as a comment IS the action —
// Enter's backend watches for them. Note the INCONSISTENT formatting: two use
// underscores and two use spaces. That is upstream's shape, not a typo here;
// "fixing" it means the button silently does nothing, because the backend
// simply never matches the string.
export const WAITING_FOR_EMR_UPDATE = 'WAITING_FOR_EMR_UPDATE';
export const RESUME_CLAIM = 'RESUME CLAIM';
export const MOVE_TO_COLLECTIONS = 'MOVE TO COLLECTIONS';
export const UPLOADED_MEDICAL_RECORDS = 'UPLOADED_MEDICAL_RECORDS';

/** Categories that unlock each optional resolve path (platform parity, rev 2). */
const RESUME_OPTION_CATEGORIES = [
  'Claim Halted',
  'Claim Denied - Rules Matched',
  'Claim Rejected - Rules Matched',
] as const;
const COLLECTIONS_OPTION_CATEGORIES = ['Inactive/Invalid Subscriber'] as const;
const RECORDS_UPLOAD_CATEGORIES = ['Missing Medical Records'] as const;

/**
 * Close-blocking is NARROWER than the resume option and must stay its own list.
 *
 * Rev 2 grew RESUME_OPTION_CATEGORIES to include denials and rejections. Those
 * tasks CAN be closed by hand; only a halted claim cannot, because Enter
 * re-halts it and sends a fresh task. Sharing one constant would silently start
 * blocking close on every denial and rejection — Platform keeps them separate
 * for exactly this reason.
 */
const CLOSE_BLOCKED_CATEGORIES = ['Claim Halted'] as const;

export type ResolveSuccessKind = 'emr' | 'resume' | 'collections' | 'upload';

// ─── Parsed shapes ──────────────────────────────────────────────────────────

export interface TaskReasonProblem {
  /** Field / role label, e.g. "Rendering provider" or "Facility". */
  slot: string;
  /** Optional person/org name when available. */
  who?: string;
  /** Invalid value to show struck-through (e.g. NPI). */
  value?: string;
  /** Short explanation of why it failed. */
  why: string;
}

export interface ParsedTaskReason {
  /** Human-readable error summary (the text outside the fence). */
  headline: string;
  /** Card title when we can classify the error. */
  title?: string;
  /** Label for the explain line (e.g. "Coder rule"). */
  explainLabel?: string;
  /** Body text under the explain label. */
  explain?: string;
  /** Footer "Missing" cell value when known. */
  missingLabel?: string;
  /** Structured fix-list rows. */
  problems?: TaskReasonProblem[];
  /** Raw fenced content (engine output or claim payload). */
  payloadRaw?: string;
  /** Parsed JSON from a Payload fence, when valid. */
  payloadJson?: unknown;
  /** Collapsible raw section label. */
  rawLabel?: string;
  /** Which structured body shape matched. */
  kind: 'payload' | 'coding_engine';
}

const PAYLOAD_BLOCK_RE =
  /(?:^|\n)\s*Payload:\s*(?:\r?\n)+\s*```(?:json|javascript|js)?[ \t]*\r?\n?([\s\S]*?)```/i;

const CODING_ENGINE_FENCE_RE =
  /(?:The coding engine has returned the following error:\s*)?```([\s\S]*?)```/i;

/** Path line that starts a violation block: "<path> is invalid." */
const INVALID_PATH_LINE_RE = /^(.+?)\s+is\s+invalid\.\s*$/i;

const INDEXED_PATH_RE = /^([A-Za-z_][\w.]*)\s*\[\s*(\d+)\s*\]$/;

const CODING_ENGINE_EXPLAIN =
  'ENTER’s coding engine flagged one or more fields on this claim before submission. Correct them in your EMR, or fix the claim in ENTER.';

// ─── Comment ordering ───────────────────────────────────────────────────────

/**
 * Chronologically FIRST comment, not array index 0.
 *
 * The distinction is load-bearing: the opening comment is the task's body, and
 * taking `comments[0]` trusts an ordering Enter never promised. Comments with a
 * missing or unparseable `created` sort last rather than winning by accident.
 */
export function getEarliestComment<T extends { created?: string | null }>(
  comments: T[] | undefined | null
): T | undefined {
  if (!comments?.length) return undefined;
  let best: T | undefined;
  let bestTime = Number.POSITIVE_INFINITY;
  for (const comment of comments) {
    const parsed = comment.created ? Date.parse(String(comment.created)) : Number.NaN;
    const time = Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
    if (best === undefined || time < bestTime) {
      best = comment;
      bestTime = time;
    }
  }
  return best;
}

// ─── Path → human label ─────────────────────────────────────────────────────

function titleCasePath(path: string): string {
  return path
    .replace(/[_.]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, ch => ch.toUpperCase());
}

/** Indexed paths like `providers [0]` → `Provider 1` (order is not role-stable). */
function labelForIndexedEntity(entity: string, index: number): string {
  const singular = entity.replace(/s$/i, '');
  return `${titleCasePath(singular)} ${index + 1}`;
}

function pathToSlot(path: string): string {
  const trimmed = path.trim();
  const indexed = trimmed.match(INDEXED_PATH_RE);
  if (indexed?.[1] && indexed[2]) {
    return labelForIndexedEntity(indexed[1], Number.parseInt(indexed[2], 10));
  }
  return titleCasePath(trimmed);
}

function splitMissingFields(line: string): string[] | null {
  const match = line.match(/^Missing fields:\s*(.+)$/i);
  if (!match?.[1]) return null;
  return match[1]
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
}

function parseInvalidFormatLine(
  line: string
): { field?: string; value?: string; why: string } | null {
  // Keep the engine's own lead-in phrase as `why` — readable, and no copy we invented.
  const match = line.match(
    /^(Invalid format for field):\s*([^\n—\-]+?)(?:\s*[—\-]\s*Invalid\s+[^:]+:\s*(.+))?$/i
  );
  if (!match?.[1]) return null;
  return {
    why: match[1],
    ...(match[2] ? { field: match[2].trim() } : {}),
    ...(match[3] ? { value: match[3].trim() } : {}),
  };
}

/**
 * Parse coding-engine fenced dumps shaped as:
 *   <path> is invalid.
 *     <reason line(s)>
 * Handles providers[N], attributes, services[N], "Missing fields" lists, etc.
 */
export function parseInvalidRowsBlock(raw: string): TaskReasonProblem[] {
  const trimmed = (raw || '').trim();
  if (!trimmed) return [];

  // Some payloads keep literal "\n" escapes inside the fence; normalize first.
  const normalized = trimmed.replace(/\\n/g, '\n');
  const lines = normalized.split(/\r?\n/);
  const blocks: { path: string; reasons: string[] }[] = [];
  let current: { path: string; reasons: string[] } | null = null;

  for (const line of lines) {
    const pathMatch = line.trim().match(INVALID_PATH_LINE_RE);
    if (pathMatch?.[1]) {
      if (current) blocks.push(current);
      current = { path: pathMatch[1].trim(), reasons: [] };
      continue;
    }
    if (current) {
      const reason = line.trim();
      if (reason) current.reasons.push(reason);
    }
  }
  if (current) blocks.push(current);

  const problems: TaskReasonProblem[] = [];

  for (const block of blocks) {
    const pathSlot = pathToSlot(block.path);

    if (!block.reasons.length) {
      problems.push({ slot: pathSlot, why: 'is invalid' });
      continue;
    }

    for (const reason of block.reasons) {
      const missingFields = splitMissingFields(reason);
      if (missingFields) {
        for (const field of missingFields) {
          problems.push({
            // Keep the parent path (patient / attributes / …) as the slot and put
            // the concrete missing field in `who`, so nothing is dropped.
            slot: pathSlot,
            who: titleCasePath(field),
            why: 'Missing fields',
          });
        }
        continue;
      }

      const format = parseInvalidFormatLine(reason);
      if (format) {
        problems.push({
          slot: pathSlot,
          ...(format.field ? { who: titleCasePath(format.field) } : {}),
          ...(format.value ? { value: format.value } : {}),
          why: format.why,
        });
        continue;
      }

      const npiOnly = reason.match(/^(Invalid\s+NPI):\s*([0-9]+)/i);
      if (npiOnly?.[1] && npiOnly[2]) {
        problems.push({ slot: pathSlot, value: npiOnly[2], why: npiOnly[1] });
        continue;
      }

      problems.push({ slot: pathSlot, why: reason });
    }
  }

  // Fallback: the blob mentions an NPI but has no "<path> is invalid." headers.
  if (!problems.length) {
    const npiMatch = trimmed.match(/(Invalid\s+NPI):\s*([0-9]+)/i);
    if (npiMatch?.[1] && npiMatch[2]) {
      problems.push({ slot: 'Provider NPI', value: npiMatch[2], why: npiMatch[1] });
    }
  }

  return problems;
}

function parsePayloadReason(body: string): ParsedTaskReason | null {
  const match = body.match(PAYLOAD_BLOCK_RE);
  if (!match || match.index === undefined) return null;

  const payloadRaw = (match[1] || '').trim();
  let payloadJson: unknown;
  if (payloadRaw) {
    try {
      payloadJson = JSON.parse(payloadRaw);
    } catch {
      // Keep the raw text only when the JSON is invalid.
    }
  }

  const beforePayload = body.slice(0, match.index).trim();
  const headline = beforePayload.replace(/^Validation errors:\s*/i, '').trim();

  if (!headline && !payloadRaw) return null;

  return {
    kind: 'payload',
    headline: headline || 'Validation error',
    title: 'Validation errors',
    ...(payloadRaw ? { payloadRaw } : {}),
    ...(payloadJson !== undefined ? { payloadJson } : {}),
  };
}

function parseCodingEngineReason(body: string): ParsedTaskReason | null {
  const hasCodingEnginePhrase = /coding engine/i.test(body);
  const hasInvalidFence = /```[\s\S]*?\bis\s+invalid\./i.test(body);
  if (!hasCodingEnginePhrase && !hasInvalidFence) return null;

  const match = body.match(CODING_ENGINE_FENCE_RE);
  if (!match || match.index === undefined) return null;

  const payloadRaw = (match[1] || '').trim();
  if (!payloadRaw) return null;

  const problems = parseInvalidRowsBlock(payloadRaw);
  const after = body
    .slice(match.index + match[0].length)
    .replace(/^\s*\.?\s*/, '')
    .trim();
  const hasProblems = problems.length > 0;

  const headline =
    after ||
    (hasProblems
      ? `${problems.length} issue${problems.length === 1 ? '' : 's'} need${
          problems.length === 1 ? 's' : ''
        } fixing.`
      : 'The coding engine returned an error.');

  if (!hasProblems) {
    return { kind: 'coding_engine', headline, title: 'Validation errors', payloadRaw };
  }

  const missingLabel = [
    ...new Set(problems.map(p => (p.who ? `${p.slot} · ${p.who}` : p.slot))),
  ].join(', ');

  return {
    kind: 'coding_engine',
    headline,
    title: 'Coding problem',
    explainLabel: 'Coder rule',
    explain: CODING_ENGINE_EXPLAIN,
    missingLabel,
    problems,
    payloadRaw,
    rawLabel: 'Coding engine output',
  };
}

/**
 * Parse a structured opening-comment body. Returns null when the body is just
 * prose — the caller then renders it as-is, which is the right answer for a
 * human-written task.
 */
export function parseTaskReason(body?: string | null): ParsedTaskReason | null {
  if (!body || !body.trim()) return null;
  return parsePayloadReason(body) || parseCodingEngineReason(body);
}

// ─── Classification ─────────────────────────────────────────────────────────

/** Section heading, chosen from the subject + category names (platform parity). */
export function getReasonSectionTitle(subject?: string, categoryNames: string[] = []): string {
  const haystack = [subject, ...categoryNames].filter(Boolean).join(' ').toLowerCase();

  if (/halt/.test(haystack)) return "Why it's halted";
  if (/coding problem|creation problem|validation/.test(haystack)) {
    return /coding problem/.test(haystack) ? "Why it's halted" : "Why it couldn't be created";
  }
  if (/denial|denied/.test(haystack)) return 'Denial reason';
  if (/reject/.test(haystack)) return 'Rejection reason';
  return 'Rules applied';
}

/**
 * Case-INSENSITIVE substring match, mirroring Platform's `categoryMatches`.
 * Rev 1 compared case-sensitively, so a catalog that returned
 * "Claim halted" rather than "Claim Halted" silently matched nothing — and a
 * missed match here means a close button appears on a task that cannot be
 * closed, which is the failure direction that misleads the user.
 */
export function categoryMatches(name: string | undefined, matcher: string): boolean {
  if (!name) return false;
  return name.toLowerCase().includes(matcher.toLowerCase());
}

function matchesAny(categoryNames: string[], targets: readonly string[]): boolean {
  return categoryNames.some(name => targets.some(target => categoryMatches(name, target)));
}

/**
 * Claim Halted tasks CANNOT be closed manually — closing achieves nothing,
 * because the claim re-halts and Enter sends a fresh task. Platform hides its
 * done controls for these; a close button here would be a lie.
 *
 * Note this uses CLOSE_BLOCKED_CATEGORIES, not the resume list — see the
 * comment on that constant.
 */
export function isClaimHaltedCategory(categoryNames: string[]): boolean {
  return matchesAny(categoryNames, CLOSE_BLOCKED_CATEGORIES);
}

export function shouldShowResumeOption(categoryNames: string[]): boolean {
  return matchesAny(categoryNames, RESUME_OPTION_CATEGORIES);
}

export function shouldShowCollectionsOption(categoryNames: string[]): boolean {
  return matchesAny(categoryNames, COLLECTIONS_OPTION_CATEGORIES);
}

export function shouldShowRecordsUploadOption(categoryNames: string[]): boolean {
  return matchesAny(categoryNames, RECORDS_UPLOAD_CATEGORIES);
}

const RESOLVE_MARKERS = [
  WAITING_FOR_EMR_UPDATE,
  RESUME_CLAIM,
  MOVE_TO_COLLECTIONS,
  UPLOADED_MEDICAL_RECORDS,
] as const;

/**
 * EXACT match, not `includes` — this changed in rev 2 and it matters.
 *
 * A marker is the WHOLE body of a system comment. Matching by substring meant
 * any comment that merely mentioned the phrase — instruction text, a quoted
 * error, a biller typing "move to collections?" — read as a completed resolve
 * action, so the task showed a success card for something nobody did.
 */
function commentHasMarker(body: string | null | undefined, marker: string): boolean {
  return typeof body === 'string' && body.trim() === marker;
}

/**
 * True when a comment IS a resolve marker. These are machine bookkeeping, not
 * conversation — Platform hides them from the thread, and showing a raw
 * `WAITING_FOR_EMR_UPDATE` to a biller is noise.
 */
export function isResolveMarkerComment(body: string | null | undefined): boolean {
  return RESOLVE_MARKERS.some(marker => commentHasMarker(body, marker));
}

/**
 * Which resolve path already succeeded, read back from the thread's comments.
 * Scans newest-first so the most recent action wins when several were taken.
 */
export function getResolveSuccessKind(
  comments: { body?: string | null | undefined }[] | undefined | null
): ResolveSuccessKind | null {
  const list = comments || [];
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const body = list[i]?.body;
    if (commentHasMarker(body, WAITING_FOR_EMR_UPDATE)) return 'emr';
    if (commentHasMarker(body, RESUME_CLAIM)) return 'resume';
    if (commentHasMarker(body, MOVE_TO_COLLECTIONS)) return 'collections';
    if (commentHasMarker(body, UPLOADED_MEDICAL_RECORDS)) return 'upload';
  }
  return null;
}

/**
 * Which resolve options a task offers, as ONE decision.
 *
 * The composition is not obvious and Platform states why: a collections task
 * ALSO gets the bypass path, "which keeps the Option 1–4 numbering stable".
 * Computing the three flags independently at the call site is how they drift
 * apart, so this returns them together.
 */
export function getResolveOptions(categoryNames: string[]): {
  showRecordsUpload: boolean;
  showCollections: boolean;
  showResume: boolean;
} {
  const showRecordsUpload = shouldShowRecordsUploadOption(categoryNames);
  const showCollections = !showRecordsUpload && shouldShowCollectionsOption(categoryNames);
  const showResume =
    !showRecordsUpload && (showCollections || shouldShowResumeOption(categoryNames));
  return { showRecordsUpload, showCollections, showResume };
}

/** Per-path confirmation copy, verbatim from Platform's SUCCESS_COPY. */
export const RESOLVE_SUCCESS_COPY: Record<
  ResolveSuccessKind,
  { title: string; body: string }
> = {
  emr: {
    title: 'Marked as updated in your EMR',
    body: 'Your EMR syncs to ENTER automatically — instantly on some systems, within 12–24 hours on others. ENTER clears the claim and resubmits once it lands.',
  },
  resume: {
    title: 'Claim resumed — check bypassed',
    body: 'Nothing on the claim was changed. ENTER will release it as-is and bypass this check. If the payer denies, ENTER opens a denial task.',
  },
  collections: {
    title: 'Moved to collections',
    body: 'ENTER will flip responsibility to the patient/guarantor and route this claim to collections. An invoice is generated and sent — nothing else needed from you on this task.',
  },
  upload: {
    title: 'Document uploaded to ENTER',
    body: 'The document is attached to the claim and resubmission is queued — nothing else needed from you. ENTER will update this task when the payer acknowledges.',
  },
};

export function hasResolveSuccess(
  comments: { body?: string | null | undefined }[] | undefined | null
): boolean {
  return getResolveSuccessKind(comments) !== null;
}
